import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Button } from '@textadapt/ui';
import { ThemeToggle } from './ThemeToggle';
import { Menu, X } from 'lucide-react';

const navItems = [
  { label: 'Features', path: '/features' },
  { label: 'Working', path: '/how-it-works' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Security', path: '/security' },
  { label: 'Contact', path: '/contact' }
];

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl transition-all duration-200 ${
          scrolled
            ? 'border-border shadow-lg'
            : 'border-transparent'
        }`}
      >
        {/* Gradient overlay */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 opacity-50" />

        <div className="relative mx-auto max-w-7xl px-4 py-3 lg:px-8">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6 lg:gap-10">
            {/* Logo */}
            <Link
              to="/"
              className="group flex items-center gap-3 no-underline hover:no-underline"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-secondary font-bold text-white text-sm uppercase tracking-wider shadow-lg shadow-primary/30 transition-transform duration-200 group-hover:scale-105">
                TA
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight text-foreground">
                  textadapt
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden items-center justify-center lg:flex">
              <ul className="flex items-center gap-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `no-underline hover:no-underline inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-all duration-200 ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-primary'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Desktop Actions */}
            <div className="hidden items-center gap-3 lg:flex">
              <ThemeToggle />
              <Button variant="ghost" size="sm" as="a" href="https://platform.textadapt.com">
                Sign In
              </Button>
              <Button variant="primary" size="sm" as="a" href="/contact">
                Request Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-transparent text-foreground transition-all duration-200 hover:scale-105 hover:border-primary/30 hover:bg-accent active:scale-95 lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Mobile Menu */}
          <div className="fixed left-0 right-0 top-[4.25rem] z-40 animate-in slide-in-from-top-4 border-b border-border bg-card shadow-2xl lg:hidden">
            <div className="mx-auto max-w-7xl px-4 py-6">
              {/* Status Badge */}
              <div className="mb-5 flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-4 py-3 dark:border-green-900 dark:bg-green-950/20">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Platform status
                </span>
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                  Operational
                </span>
              </div>

              {/* Mobile Links */}
              <ul className="mb-5 space-y-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `no-underline hover:no-underline flex items-center justify-between rounded-xl border px-4 py-3 text-base font-semibold transition-all duration-200 ${
                          isActive
                            ? 'border-primary/20 bg-primary/10 text-primary'
                            : 'border-transparent bg-muted text-muted-foreground hover:border-primary/20 hover:bg-primary/5 hover:text-primary'
                        }`
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>

              {/* Mobile Actions */}
              <div className="space-y-3 border-t border-border pt-5">
                <div className="flex justify-center pb-2">
                  <ThemeToggle />
                </div>
                <Button
                  variant="ghost"
                  size="md"
                  as="a"
                  href="https://platform.textadapt.com"
                  fullWidth
                >
                  Sign In
                </Button>
                <Button variant="primary" size="md" as="a" href="/contact" fullWidth>
                  Request Demo
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
